import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db, handleFirebaseError } from '../firebase.config';
import UserMigrationUtils from './userMigrationUtils';
import { SessionStorage } from './unifiedStorage';
import { normalizeEmail, getAuthErrorMessage as getEnhancedAuthErrorMessage } from './emailUtils';

// 🔐 Authentication Service for ACCTOO
export class AuthService {
  static currentUser = null;
  static authStateListeners = new Set();

  static async setCrossDomainAuth(user) {
    try {
      if (user) {
        const token = await user.getIdToken();
        const expiry = Date.now() + (60 * 60 * 1000); // 1 hour
        
        // Use unified storage for cross-platform persistence
        await SessionStorage.setAuthToken(token, expiry);
        await SessionStorage.setUserData({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        });
        
        // Fallback to localStorage for web compatibility
        try {
          localStorage.setItem('acctoo_auth_token', token);
          localStorage.setItem('acctoo_user_id', user.uid);
        } catch (_) {}
        
        // Set cross-domain cookie (only for web)
        try {
          if (typeof document !== 'undefined') {
            const cookie = `acctoo_auth_token=${token}; path=/; domain=.acctoo.com`;
            document.cookie = cookie;
          }
        } catch (_) {}
      } else {
        // Clear all auth data
        await SessionStorage.clearAuthToken();
        await SessionStorage.clearUserData();
        
        // Fallback cleanup
        try {
          localStorage.removeItem('acctoo_auth_token');
          localStorage.removeItem('acctoo_user_id');
        } catch (_) {}
        try {
          if (typeof document !== 'undefined') {
            document.cookie = 'acctoo_auth_token=; Max-Age=0; path=/; domain=.acctoo.com';
          }
        } catch (_) {}
      }
    } catch (e) {
      console.error('Cross-domain auth sync error:', e);
    }
  }

  // Initialize authentication state
  static init() {
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.notifyAuthStateListeners(user);
      // Cross-domain token sharing
      this.setCrossDomainAuth(user);
      
      if (user) {
        console.log('🔥 User authenticated:', user.email);
        this.updateUserLastLogin(user.uid);
      } else {
        console.log('🔥 User signed out');
      }
    });
  }

  // Add auth state listener with proper cleanup
  static onAuthStateChanged(callback) {
    if (typeof callback !== 'function') {
      console.error('Auth state listener callback must be a function');
      return () => {};
    }

    this.authStateListeners.add(callback);
    
    // Return cleanup function
    return () => {
      this.authStateListeners.delete(callback);
    };
  }

  // Notify all auth state listeners with error handling
  static notifyAuthStateListeners(user) {
    const listenersToRemove = [];
    
    this.authStateListeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        console.error('Auth state listener error:', error);
        // Mark for removal if it throws an error
        listenersToRemove.push(callback);
      }
    });
    
    // Remove listeners that threw errors
    listenersToRemove.forEach(callback => {
      this.authStateListeners.delete(callback);
    });
  }

  // Clean up all auth state listeners
  static cleanupAuthStateListeners() {
    console.log(`🧹 Cleaning up ${this.authStateListeners.size} auth state listeners`);
    this.authStateListeners.clear();
  }

  // Get count of active listeners (for debugging)
  static getListenerCount() {
    return this.authStateListeners.size;
  }

  // User login with email/password
  static async login(email, password, companyId = null) {
    try {
      // Normalize email to lowercase for consistent authentication
      const normalizedEmail = normalizeEmail(email);
      console.log('🔥 Attempting login for:', normalizedEmail);
      
      const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;
      
      // Log successful login
      await this.logUserLogin(user.uid, companyId, true);
      
      // Check if user exists in our system
      const userProfile = await this.getUserProfile(user.uid);
      
      if (!userProfile.success) {
        // New user or user not migrated - migrate to new system
        console.log('🔄 Migrating user to new system:', user.uid);
        const migrationResult = await UserMigrationUtils.migrateExistingUser(
          user.uid,
          {
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified
          },
          companyId
        );
        
        if (migrationResult.success) {
          userProfile.data = migrationResult.profile;
        } else {
          console.error('❌ User migration failed:', migrationResult.error);
        }
      } else {
        // Existing user - update last login
        await this.updateUserLastLogin(user.uid);
      }
      
      return { success: true, user, profile: userProfile.data || userProfile };
    } catch (error) {
      console.error('Login error:', error);
      await this.logUserLogin(email, companyId, false, error.message);
      
      return { 
        success: false, 
        error: getEnhancedAuthErrorMessage(error.code, error.message) 
      };
    }
  }

  // User login with phone/password
  static async loginWithPhone(phoneNumber, password, companyId = null) {
    try {
      console.log('🔥 Attempting phone login for:', phoneNumber);
      
      // First, get the email associated with this phone number
      const phoneDoc = await getDoc(doc(db, 'phone_index', phoneNumber));
      if (!phoneDoc.exists()) {
        return { 
          success: false, 
          error: 'Phone number not found in our records' 
        };
      }
      
      const phoneData = phoneDoc.data();
      const email = phoneData.email;
      
      if (!email) {
        return { 
          success: false, 
          error: 'No email associated with this phone number' 
        };
      }
      
      // Now login with email and password
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Log successful login
      await this.logUserLogin(user.uid, companyId, true);
      
      // Check if user exists in our system
      const userProfile = await this.getUserProfile(user.uid);
      
      if (!userProfile.success) {
        // New user or user not migrated - migrate to new system
        console.log('🔄 Migrating user to new system:', user.uid);
        const migrationResult = await UserMigrationUtils.migrateExistingUser(
          user.uid,
          {
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified
          },
          companyId
        );
        
        if (migrationResult.success) {
          userProfile.data = migrationResult.profile;
        } else {
          console.error('❌ User migration failed:', migrationResult.error);
        }
      } else {
        // Existing user - update last login
        await this.updateUserLastLogin(user.uid);
      }
      
      return { success: true, user, profile: userProfile.data || userProfile };
    } catch (error) {
      console.error('Phone login error:', error);
      await this.logUserLogin(phoneNumber, companyId, false, error.message);
      
      return { 
        success: false, 
        error: getEnhancedAuthErrorMessage(error.code, error.message) 
      };
    }
  }

  // User registration
  static async register(email, password, userData, companyId = null) {
    try {
      // Normalize email to lowercase for consistent authentication
      const normalizedEmail = normalizeEmail(email);
      console.log('🔥 Attempting registration for:', normalizedEmail);
      
      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;
      
      // Create user profile
      await this.createUserProfile(user, companyId, userData);
      
      // Send email verification
      await sendEmailVerification(user);
      
      return { success: true, user };
    } catch (error) {
      console.error('Registration error:', error);
      
      return { 
        success: false, 
        error: getEnhancedAuthErrorMessage(error.code, error.message) 
      };
    }
  }

  // User logout
  static async logout() {
    try {
      await signOut(auth);
      // Clear cross-domain tokens
      this.setCrossDomainAuth(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  }

  // Create user profile
  static async createUserProfile(user, companyId = null, additionalData = {}) {
    try {
      const userProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || additionalData.displayName || '',
        phoneNumber: user.phoneNumber || additionalData.phoneNumber || '',
        companyId: companyId,
        role: additionalData.role || 'user',
        status: 'active',
        emailVerified: user.emailVerified,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        ...additionalData
      };

      // Save to Firestore
      await setDoc(doc(db, 'users', user.uid), userProfile);
      
      // If companyId is provided, add user to company
      if (companyId) {
        await this.addUserToCompany(user.uid, companyId, additionalData.role || 'employee');
      }
      
      console.log('🔥 User profile created:', user.uid);
      return { success: true, profile: userProfile };
    } catch (error) {
      console.error('Profile creation error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user profile
  static async getUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      }
      return { success: false, error: 'User profile not found' };
    } catch (error) {
      console.error('Profile retrieval error:', error);
      return { success: false, error: error.message };
    }
  }

  // Update user profile
  static async updateUserProfile(uid, updateData) {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message };
    }
  }

  // Add user to company
  static async addUserToCompany(uid, companyId, role = 'employee') {
    try {
      const companyUserRef = doc(db, 'companies', companyId, 'employees', uid);
      await setDoc(companyUserRef, {
        uid,
        role,
        status: 'active',
        addedAt: serverTimestamp(),
        permissions: this.getDefaultPermissions(role)
      });
      
      // Update user_companies collection
      const userCompaniesRef = doc(db, 'user_companies', uid);
      await setDoc(userCompaniesRef, {
        companies: [companyId],
        activeCompany: companyId,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      console.log('🔥 User added to company:', uid, companyId);
      return { success: true };
    } catch (error) {
      console.error('Add user to company error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user companies
  static async getUserCompanies(uid) {
    try {
      const userCompaniesDoc = await getDoc(doc(db, 'user_companies', uid));
      if (userCompaniesDoc.exists()) {
        return { success: true, data: userCompaniesDoc.data() };
      }
      return { success: false, error: 'User companies not found' };
    } catch (error) {
      console.error('User companies retrieval error:', error);
      return { success: false, error: error.message };
    }
  }

  // Switch active company
  static async switchCompany(uid, companyId) {
    try {
      const userCompaniesRef = doc(db, 'user_companies', uid);
      await updateDoc(userCompaniesRef, {
        activeCompany: companyId,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Company switch error:', error);
      return { success: false, error: error.message };
    }
  }

  // Password reset
  static async resetPassword(email) {
    try {
      // Normalize email to lowercase for consistent authentication
      const normalizedEmail = normalizeEmail(email);
      await sendPasswordResetEmail(auth, normalizedEmail);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: getEnhancedAuthErrorMessage(error.code, error.message) };
    }
  }

  // Update user last login
  static async updateUserLastLogin(uid) {
    try {
      const userRef = doc(db, 'users', uid);
      
      // Check if document exists first
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Document exists, update it
        await updateDoc(userRef, {
          lastLoginAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Document doesn't exist, create it with minimal data
        await setDoc(userRef, {
          uid: uid,
          lastLoginAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      // Only log if it's not an offline error (offline errors are expected)
      if (error.code !== 'unavailable' && !error.message?.includes('offline')) {
        console.error('Update last login error:', error);
      }
    }
  }

  // Log user login attempt
  static async logUserLogin(identifier, companyId, success, errorMessage = null) {
    try {
      await setDoc(doc(collection(db, 'access_logs')), {
        identifier,
        companyId,
        action: 'login',
        success,
        errorMessage,
        timestamp: serverTimestamp(),
        ipAddress: 'client_ip', // TODO: Get actual IP
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Login logging error:', error);
    }
  }

  // Get default permissions based on role
  static getDefaultPermissions(role) {
    const permissions = {
      owner: ['all'],
      manager: ['read', 'write', 'delete', 'manage_employees'],
      employee: ['read', 'write'],
      viewer: ['read']
    };
    
    return permissions[role] || ['read'];
  }

  // Get authentication error messages
  // Using enhanced error messages from emailUtils
  static getAuthErrorMessage(errorCode, defaultMessage) {
    return getEnhancedAuthErrorMessage(errorCode, defaultMessage);
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.currentUser;
  }

  // Get current user
  static getCurrentUser() {
    return this.currentUser;
  }

  // Check if user has permission in company
  static async hasCompanyPermission(uid, companyId, permission) {
    try {
      const employeeDoc = await getDoc(doc(db, 'companies', companyId, 'employees', uid));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        return employeeData.permissions.includes('all') || employeeData.permissions.includes(permission);
      }
      return false;
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  }
}

// Initialize auth service when module loads
AuthService.init();

export default AuthService;
